import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, BookOpen, Shield, Eye, EyeOff, FileText, Facebook, MessageSquare, Share2, Check } from 'lucide-react';

const EDUCATION_CATEGORIES = ['Market Updates', 'Insurance', 'Legal', 'Investment Tips', 'Retirement Planning', 'Tax Information'];
const SERVICE_CATEGORIES = ['Legal Services', 'Insurance', 'Investment', 'Property Management', 'Tax Advisory', 'Retirement Planning'];

const ContentManager = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('education');
  const [fbConfigured, setFbConfigured] = useState(false);
  const [lineConfigured, setLineConfigured] = useState(false);

  const [formData, setFormData] = useState({
    destination: 'education',
    title: '',
    content: '',
    excerpt: '',
    category: 'Market Updates',
    author: 'NOK Thailand Team',
    image_url: '',
    published: true,
    featured: false,
    // Service-specific fields
    icon: 'TrendingUp',
    color: 'from-blue-600 to-blue-800',
    features: [],
    contact_name: 'NOK Thailand Team',
    contact_phone: '',
    documents: [],
    order: 0,
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchAll();
    checkFbStatus();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [postsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/blog/posts`),
        axios.get(`${API}/services?published_only=false`)
      ]);
      setPosts(postsRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      toast.error('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  };

  const checkFbStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/social/status`, { headers: { Authorization: `Bearer ${token}` } });
      setFbConfigured(res.data.facebook_configured);
      setLineConfigured(res.data.line_configured);
    } catch { setFbConfigured(false); setLineConfigured(false); }
  };

  const openCreateDialog = (dest) => {
    setEditingItem(null);
    setFormData({
      destination: dest || activeTab,
      title: '',
      content: '',
      excerpt: '',
      category: dest === 'services' ? 'Legal Services' : 'Market Updates',
      author: 'NOK Thailand Team',
      image_url: '',
      published: true,
      featured: false,
      icon: 'TrendingUp',
      color: 'from-blue-600 to-blue-800',
      features: [],
      contact_name: 'NOK Thailand Team',
      contact_phone: '',
      documents: [],
      order: 0,
    });
    setDialogOpen(true);
  };

  const openEditEducation = (post) => {
    setEditingItem({ ...post, _type: 'education' });
    setFormData({
      destination: 'education',
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      category: post.category,
      author: post.author,
      image_url: post.image_url || '',
      published: post.published,
      featured: post.featured,
      icon: 'TrendingUp', color: 'from-blue-600 to-blue-800', features: [], contact_name: '', contact_phone: '', documents: [], order: 0,
    });
    setDialogOpen(true);
  };

  const openEditService = (service) => {
    setEditingItem({ ...service, _type: 'services' });
    setFormData({
      destination: 'services',
      title: service.title,
      content: service.description,
      excerpt: '',
      category: service.title,
      author: 'NOK Thailand Team',
      image_url: service.image_url || '',
      published: service.published,
      featured: false,
      icon: service.icon || 'TrendingUp',
      color: service.color || 'from-blue-600 to-blue-800',
      features: service.features || [],
      contact_name: service.contact_name || '',
      contact_phone: service.contact_phone || '',
      documents: service.documents || [],
      order: service.order || 0,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (formData.destination === 'education') {
        const payload = {
          title: formData.title,
          content: formData.content,
          excerpt: formData.excerpt,
          category: formData.category,
          author: formData.author,
          image_url: formData.image_url,
          published: formData.published,
          featured: formData.featured,
        };
        if (editingItem && editingItem._type === 'education') {
          await axios.put(`${API}/blog/posts/${editingItem.id}`, payload, { headers });
          toast.success('Education article updated');
        } else {
          await axios.post(`${API}/blog/posts`, payload, { headers });
          toast.success('Education article created');
        }
      } else {
        const payload = {
          title: formData.title,
          description: formData.content,
          icon: formData.icon,
          color: formData.color,
          features: formData.features,
          contact_name: formData.contact_name,
          contact_phone: formData.contact_phone,
          image_url: formData.image_url,
          documents: formData.documents,
          order: formData.order,
          published: formData.published,
        };
        if (editingItem && editingItem._type === 'services') {
          await axios.put(`${API}/services/${editingItem.id}`, payload, { headers });
          toast.success('Service updated');
        } else {
          await axios.post(`${API}/services`, payload, { headers });
          toast.success('Service created');
        }
      }
      setDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    const token = localStorage.getItem('token');
    try {
      const endpoint = type === 'education' ? `${API}/blog/posts/${id}` : `${API}/services/${id}`;
      await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted successfully');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const postBlogToFacebook = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/facebook/publish-blog/${postId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Posted to Facebook!');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post to Facebook');
    }
  };

  const postBlogToLine = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/line/publish-blog/${postId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Posted to LINE Voom!');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post to LINE');
    }
  };

  const postBlogToAll = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/social/publish-blog/${postId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.facebook?.success) toast.success('Posted to Facebook!');
      if (res.data.line?.success) toast.success('Posted to LINE!');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({ ...formData, features: [...formData.features, newFeature.trim()] });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) });
  };

  const inputCls = "bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 rounded-xl";
  const categories = formData.destination === 'education' ? EDUCATION_CATEGORIES : SERVICE_CATEGORIES;

  const iconOptions = ['TrendingUp', 'Shield', 'Heart', 'Scale', 'Building2', 'Users', 'FileText', 'Briefcase', 'Award', 'Globe'];
  const colorOptions = [
    { label: 'Blue', value: 'from-blue-600 to-blue-800' },
    { label: 'Green', value: 'from-green-600 to-emerald-800' },
    { label: 'Orange', value: 'from-amber-600 to-orange-800' },
    { label: 'Purple', value: 'from-purple-600 to-purple-800' },
    { label: 'Red', value: 'from-red-600 to-red-800' },
    { label: 'Teal', value: 'from-teal-600 to-teal-800' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0E27] via-[#1a1f3a] to-[#0A0E27]">
      <header className="bg-black/40 backdrop-blur-md border-b border-[#D4AF37]/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl font-bold text-[#D4AF37]" data-testid="content-manager-title">Content Manager</h1>
              <p className="text-[#C9A961] text-sm">Create & manage articles for Education and Services</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => openCreateDialog(activeTab)}
                className="bg-gradient-to-r from-[#D4AF37] to-[#C9A961] hover:from-[#C9A961] hover:to-[#D4AF37] text-black font-semibold" data-testid="new-article-btn">
                <Plus className="w-4 h-4 mr-2" /> New Article
              </Button>
              <Button onClick={() => navigate('/admin/dashboard')} variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black" data-testid="back-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Switcher */}
        <div className="flex gap-4 mb-8" data-testid="content-tabs">
          <button
            onClick={() => setActiveTab('education')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${
              activeTab === 'education'
                ? 'bg-[#D4AF37] text-[#0A0E27]'
                : 'bg-white/5 text-[#94A3B8] hover:bg-white/10 hover:text-white border border-white/10'
            }`}
            data-testid="tab-education"
          >
            <BookOpen className="w-4 h-4" /> Education Articles ({posts.length})
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${
              activeTab === 'services'
                ? 'bg-[#D4AF37] text-[#0A0E27]'
                : 'bg-white/5 text-[#94A3B8] hover:bg-white/10 hover:text-white border border-white/10'
            }`}
            data-testid="tab-services"
          >
            <Shield className="w-4 h-4" /> Services ({services.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#D4AF37] border-r-transparent"></div>
          </div>
        ) : activeTab === 'education' ? (
          /* Education Articles Grid */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="education-grid">
            {posts.length === 0 ? (
              <div className="col-span-full text-center py-16">
                <BookOpen className="w-16 h-16 text-[#D4AF37]/20 mx-auto mb-4" />
                <p className="text-[#94A3B8] text-lg">No education articles yet</p>
                <Button onClick={() => openCreateDialog('education')} className="mt-4 bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-full">
                  <Plus className="w-4 h-4 mr-2" /> Create First Article
                </Button>
              </div>
            ) : posts.map((post) => (
              <div key={post.id} className="bg-[#131A38] rounded-xl border border-white/5 hover:border-[#D4AF37]/30 overflow-hidden transition-all duration-300" data-testid={`education-card-${post.id}`}>
                {post.image_url && <img src={post.image_url} alt={post.title} className="w-full h-40 object-cover" />}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="px-2 py-0.5 bg-[#D4AF37]/15 text-[#D4AF37] text-xs font-medium rounded-full">{post.category}</span>
                    {post.featured && <span className="px-2 py-0.5 bg-orange-500/15 text-orange-400 text-xs font-medium rounded-full">Featured</span>}
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${post.published ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                      {post.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2 line-clamp-2">{post.title}</h3>
                  <p className="text-[#94A3B8] text-sm mb-3 line-clamp-2">{post.excerpt || post.content?.substring(0, 100)}</p>
                  <p className="text-[#475569] text-xs mb-4">{formatDate(post.created_at)}</p>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={() => openEditEducation(post)} className="bg-white/5 hover:bg-white/10 text-white text-xs rounded-lg" data-testid={`edit-edu-${post.id}`}>
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" onClick={() => handleDelete(post.id, 'education')} className="bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs rounded-lg" data-testid={`delete-edu-${post.id}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                    <Button size="sm" onClick={() => postBlogToFacebook(post.id)}
                      disabled={post.facebook_posted}
                      className={`text-xs rounded-lg ${post.facebook_posted ? 'bg-blue-600/10 text-blue-300 opacity-60' : 'bg-blue-600/20 hover:bg-blue-600/30 text-blue-400'}`}
                      data-testid={`fb-edu-${post.id}`}>
                      {post.facebook_posted ? <Check className="w-3 h-3 mr-1" /> : <Facebook className="w-3 h-3 mr-1" />} FB
                    </Button>
                    <Button size="sm" onClick={() => postBlogToLine(post.id)}
                      disabled={post.line_posted}
                      className={`text-xs rounded-lg ${post.line_posted ? 'bg-green-600/10 text-green-300 opacity-60' : 'bg-green-600/20 hover:bg-green-600/30 text-green-400'}`}
                      data-testid={`line-edu-${post.id}`}>
                      {post.line_posted ? <Check className="w-3 h-3 mr-1" /> : <MessageSquare className="w-3 h-3 mr-1" />} LINE
                    </Button>
                    <Button size="sm" onClick={() => postBlogToAll(post.id)}
                      disabled={post.facebook_posted && post.line_posted}
                      className={`text-xs rounded-lg ${post.facebook_posted && post.line_posted ? 'bg-[#D4AF37]/10 text-[#D4AF37]/40 opacity-60' : 'bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37]'}`}
                      data-testid={`post-all-edu-${post.id}`}>
                      <Share2 className="w-3 h-3 mr-1" /> All
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Services Grid */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
            {services.length === 0 ? (
              <div className="col-span-full text-center py-16">
                <Shield className="w-16 h-16 text-[#D4AF37]/20 mx-auto mb-4" />
                <p className="text-[#94A3B8] text-lg">No services yet</p>
                <Button onClick={() => openCreateDialog('services')} className="mt-4 bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-full">
                  <Plus className="w-4 h-4 mr-2" /> Create First Service
                </Button>
              </div>
            ) : services.map((service) => (
              <div key={service.id} className="bg-[#131A38] rounded-xl border border-white/5 hover:border-[#D4AF37]/30 overflow-hidden transition-all duration-300" data-testid={`service-card-${service.id}`}>
                <div className={`h-2 bg-gradient-to-r ${service.color || 'from-blue-600 to-blue-800'}`} />
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${service.published ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                      {service.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{service.title}</h3>
                  <p className="text-[#94A3B8] text-sm mb-3 line-clamp-3">{service.description}</p>
                  {service.features?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {service.features.slice(0, 3).map((f, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white/5 text-[#94A3B8] text-xs rounded-full">{f}</span>
                      ))}
                      {service.features.length > 3 && <span className="text-[#475569] text-xs">+{service.features.length - 3} more</span>}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => openEditService(service)} className="bg-white/5 hover:bg-white/10 text-white text-xs rounded-lg" data-testid={`edit-svc-${service.id}`}>
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" onClick={() => handleDelete(service.id, 'services')} className="bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs rounded-lg" data-testid={`delete-svc-${service.id}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#131A38] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#D4AF37]" data-testid="dialog-title">
              {editingItem ? 'Edit Article' : 'Create New Article'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Destination Selector */}
            {!editingItem && (
              <div>
                <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-2 block">Post To *</Label>
                <div className="flex gap-3" data-testid="destination-selector">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, destination: 'education', category: 'Market Updates' })}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all duration-300 border ${
                      formData.destination === 'education'
                        ? 'bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]'
                        : 'bg-white/5 border-white/10 text-[#94A3B8] hover:border-white/20'
                    }`}
                    data-testid="dest-education"
                  >
                    <BookOpen className="w-4 h-4" /> Education
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, destination: 'services', category: 'Legal Services' })}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all duration-300 border ${
                      formData.destination === 'services'
                        ? 'bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]'
                        : 'bg-white/5 border-white/10 text-[#94A3B8] hover:border-white/20'
                    }`}
                    data-testid="dest-services"
                  >
                    <Shield className="w-4 h-4" /> Services
                  </button>
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Title *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Article title" className={inputCls} data-testid="article-title-input" />
            </div>

            {/* Category / Topic */}
            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Topic / Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger className={inputCls} data-testid="article-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#131A38] border-white/10">
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat} className="text-white hover:bg-white/10">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Content */}
            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">
                {formData.destination === 'education' ? 'Article Content *' : 'Service Description *'}
              </Label>
              <Textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={6} placeholder="Write your content here..." className={`${inputCls} resize-y`} data-testid="article-content-input" />
            </div>

            {/* Education-specific fields */}
            {formData.destination === 'education' && (
              <>
                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Excerpt / Summary</Label>
                  <Textarea value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    rows={2} placeholder="Brief summary for previews" className={`${inputCls} resize-y`} data-testid="article-excerpt-input" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Author</Label>
                  <Input value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    className={inputCls} data-testid="article-author-input" />
                </div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-4 h-4 rounded border-white/20 accent-[#D4AF37]" data-testid="article-featured-check" />
                    <span className="text-[#94A3B8] text-sm">Featured</span>
                  </label>
                </div>
              </>
            )}

            {/* Service-specific fields */}
            {formData.destination === 'services' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Icon</Label>
                    <Select value={formData.icon} onValueChange={(v) => setFormData({ ...formData, icon: v })}>
                      <SelectTrigger className={inputCls} data-testid="service-icon-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#131A38] border-white/10">
                        {iconOptions.map(icon => (
                          <SelectItem key={icon} value={icon} className="text-white hover:bg-white/10">{icon}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Color Theme</Label>
                    <Select value={formData.color} onValueChange={(v) => setFormData({ ...formData, color: v })}>
                      <SelectTrigger className={inputCls} data-testid="service-color-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#131A38] border-white/10">
                        {colorOptions.map(c => (
                          <SelectItem key={c.value} value={c.value} className="text-white hover:bg-white/10">{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Contact Name</Label>
                    <Input value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className={inputCls} data-testid="service-contact-name" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Contact Phone</Label>
                    <Input value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="e.g., 081-941-5945" className={inputCls} data-testid="service-contact-phone" />
                  </div>
                </div>
                {/* Features */}
                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Features / Key Points</Label>
                  <div className="flex gap-2 mb-2">
                    <Input value={newFeature} onChange={(e) => setNewFeature(e.target.value)}
                      placeholder="Add a feature" className={`flex-1 ${inputCls}`}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFeature(); } }}
                      data-testid="service-feature-input" />
                    <Button type="button" onClick={addFeature} size="sm" className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27]" data-testid="add-feature-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.features.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-[#94A3B8]">
                        {f}
                        <button type="button" onClick={() => removeFeature(i)} className="text-red-400 hover:text-red-300 ml-1">&times;</button>
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Common fields */}
            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Image URL</Label>
              <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..." className={inputCls} data-testid="article-image-input" />
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  className="w-4 h-4 rounded border-white/20 accent-[#D4AF37]" data-testid="article-published-check" />
                <span className="text-[#94A3B8] text-sm">Published</span>
              </label>
            </div>

            <Button onClick={handleSave}
              className="w-full bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold h-12 rounded-full" data-testid="save-article-btn">
              {editingItem ? 'Update Article' : 'Create Article'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentManager;
